import type { GridColumnGroupingModel } from '@repo/go-table';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { TDictObject } from 'src/api/directories/types';
import { hasValue } from 'src/shared/utils/common';

import type { WellsTableStore } from '../wells-table.store';

export const useGetColumnGrouping = (store: WellsTableStore) => {
  const { t } = useTranslation();
  const { rows, directoriesObjects } = store;

  const groups: GridColumnGroupingModel = useMemo(() => {
    const groups = [
      {
        groupId: 'mainData',
        headerName: t('wells:Table.basicData'),
        children: [
          'wellName',
          'blockId',
          'fieldId',
          'clusterId',
          'subsoilUserId',
          'padId',
          'wellNumber',
          'geoTaskIndex',
          'index',
          'coordinatesT2X',
          'coordinatesT2Y',
          'coordinatesT3X',
          'coordinatesT3Y',
          'passingValue',
          'trunkLength',
          'commercialSpeed',
          'speedPerDay',
          'wellPurposeId',
          'fluidTypeId',
          'reservoirIds',
          'wellTypeId',
          'wellCategoryId',
          'pilotNumber',
          'wellEndingTypeId',
          'drillingStatusId',
          'additionalNote',
          'rigId',
          'rigNumber',
          'brigadeId',
          'maxH2s',
          'isSliderUsed',
          'foc178',
          'foc245',
          'coreSamplingIntervalId',
          'extractionVolume',
          'testingFormationWorkAmount',
          'numberOfDataInterpretationOperations',
          'daysAmountOfDataInterpretationOperation',
        ].map((field) => ({ field })),
      },
      {
        groupId: 'padPreparation',
        headerName: t('wells:Labels.padPreparation'),
        children: [
          'padReadiness',
          'padReadyDate',
          'mobilizationStartDate',
          'wellCount',
          'unplannedWellCount',
          'padTypeId',
          'equipmentLayout',
          'equipmentLayoutComment',
          'engineeringSurveysCompleted',
          'engineeringSurveysComment',
          'costCenter',
          'requiredWorks',
          'readinessComment',
          'padDocumentationReadiness',
          'landLeaseDate',
          'forestProjectDate',
          'documentationComment',
          'routeReadiness',
          'routeReadyDate',
          'routeTypeId',
          'routeComment',
          'routeDocumentationReadiness',
          'routeDocumentationDate',
          'routeDocumentationComment',
          'electricityReadiness',
          'electricityDate',
          'electricityComment',
        ].map((field) => ({ field })),
      },
    ];

    const passingByYears = rows.reduce((acc: Record<number, number>, row) => {
      const { passingByMonth } = row.data;

      if (!hasValue(passingByMonth)) {
        return acc;
      }

      for (const year in passingByMonth) {
        if (!acc[Number(year)]) {
          acc[Number(year)] = Number(year);
        }
      }

      return acc;
    }, {});

    const passingGroups: GridColumnGroupingModel = [];

    Object.values(passingByYears).forEach((year) => {
      passingGroups.push({
        groupId: `passingByYear_${year}`,
        headerName: t('wells:Labels.passingValueYear', { year }),
        children: [
          ...new Array(12).fill(0).map((_, index) => ({ field: `passing_${year}_${index}` })),
          { field: `passingByYear_${year}_total` },
          { field: `csByYear_${year}` },
        ],
      });
    });

    const { stageGroups, sectionGroups } = (() => {
      const stageIdsSet = new Set<number>();
      const sectionIdsSet = new Set<number>();

      rows.forEach((row) => {
        const { stagesByStageType, sectionsBySectionType } = row.data;

        if (!stagesByStageType || !sectionsBySectionType) {
          return;
        }

        Object.keys(stagesByStageType).forEach((stageId) => {
          stageIdsSet.add(Number(stageId));
        });

        Object.keys(sectionsBySectionType).forEach((sectionId) => {
          sectionIdsSet.add(Number(sectionId));
        });
      });

      const wellLifeCycleStagesMap = new Map<number, TDictObject>(
        directoriesObjects.wellLifeCycleStages.map((dict) => [Number(dict.id), dict]),
      );
      const sectionTypesMap = new Map<number, TDictObject>(
        directoriesObjects.sectionTypes.map((dict) => [Number(dict.id), dict]),
      );

      const stageGroups: GridColumnGroupingModel = [];
      const sectionGroups: GridColumnGroupingModel = [];

      const stageIds = Array.from(stageIdsSet).sort((a, b) => {
        const stageDictionary1Order = wellLifeCycleStagesMap.get(a)?.data['order'] as number | undefined;
        const stageDictionary2Order = wellLifeCycleStagesMap.get(b)?.data['order'] as number | undefined;

        return (stageDictionary1Order ?? Infinity) - (stageDictionary2Order ?? Infinity);
      });

      stageIds.forEach((stageId) => {
        const stageDictionary = wellLifeCycleStagesMap.get(stageId);

        if (!stageDictionary) {
          return;
        }

        stageGroups.push({
          groupId: `stage_${stageId}`,
          headerName: stageDictionary.data['name'] as string,
          children: [
            `stage_${stageId}_startDate`,
            `stage_${stageId}_endDate`,
            `stage_${stageId}_duration`,
            `stage_${stageId}_distance`,
          ].map((field) => ({ field })),
        });
      });

      sectionIdsSet.forEach((sectionId) => {
        const sectionDictionary = sectionTypesMap.get(sectionId);

        if (!sectionDictionary) {
          return;
        }

        sectionGroups.push({
          groupId: `section_${sectionId}`,
          headerName: sectionDictionary.data['name'] as string,
          children: [
            `section_${sectionId}_startDate`,
            `section_${sectionId}_endDate`,
            `section_${sectionId}_duration`,
          ].map((field) => ({ field })),
        });
      });

      return {
        stageGroups,
        sectionGroups,
      };
    })();

    const stagesGroup = {
      groupId: 'stages',
      headerName: t('wells:Labels.stages'),
      children: [...stageGroups, { field: 'headsFreeDate' }],
    };

    const sectionsGroup = {
      groupId: 'sections',
      headerName: t('wells:Labels.sections'),
      children: sectionGroups,
    };

    const passingsGroup = {
      groupId: 'passings',
      headerName: t('wells:Labels.passings'),
      children: passingGroups,
    };

    return [...groups, stagesGroup, sectionsGroup, passingsGroup];
  }, [t, rows, directoriesObjects.wellLifeCycleStages, directoriesObjects.sectionTypes]);

  return groups;
};
